import { useEffect, useRef, useCallback } from 'react';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import * as Linking from 'expo-linking';
import Constants from 'expo-constants';
import { EventSubscription } from 'expo-modules-core';
import { useShallow } from 'zustand/react/shallow';
import useNotificationStore from '@/stores/useNotificationStore';
import SentryService from '@/services/sentry/Sentry.service';
import { IS_WEB } from '@/shared/constants/utility';
import { ROUTES } from '../constants/routes';

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
});

export function useNotifications() {
  const {
    setPushToken,
    setCurrentNotification,
    setCurrentDeepLink,
    setParsedDeepLink,
    setPendingWebRedirect,
  } = useNotificationStore(
    useShallow((state) => ({
      setPushToken: state.setPushToken,
      setCurrentNotification: state.setCurrentNotification,
      setCurrentDeepLink: state.setCurrentDeepLink,
      setParsedDeepLink: state.setParsedDeepLink,
      setPendingWebRedirect: state.setPendingWebRedirect,
    })),
  );

  const notificationListener = useRef<EventSubscription>();
  const responseListener = useRef<EventSubscription>();
  const urlListener = useRef<EventSubscription>();

  const handleDeepLink = useCallback(
    (url: string) => {
      __DEV__ && console.log('Deep link received:', url);
      setCurrentDeepLink(url);

      try {
        const parsed = Linking.parse(url);
        __DEV__ && console.log('Parsed deep link:', parsed);
        setParsedDeepLink(parsed);
      } catch (error) {
        SentryService.captureError('Error parsing deep link', error);
      }
    },
    [setCurrentDeepLink, setParsedDeepLink],
  );

  const handleWebRedirect = useCallback(() => {
    if (IS_WEB && typeof window !== 'undefined') {
      const currentUrl = window.location.href;
      const currentPath = window.location.pathname;

      // Only store URLs that aren't the home page or onboarding pages
      if (
        currentPath !== '/' &&
        !currentPath.includes(ROUTES.WELCOME.path as string) &&
        !currentPath.includes(ROUTES.ONBOARDING.path as string) &&
        !currentPath.includes(ROUTES.CLIENT_ONBOARDING.path as string)
      ) {
        __DEV__ && console.log('Storing web redirect URL:', currentUrl);
        setPendingWebRedirect(currentUrl);
      }
    }
  }, [setPendingWebRedirect]);

  const registerForPushNotificationsAsync = useCallback(async () => {
    if (IS_WEB) {
      __DEV__ &&
        console.log('Push notification registration: Not supported on web');
      return null;
    }

    if (!Device.isDevice) {
      __DEV__ && console.log('Push notifications: Not a physical device');
      return null;
    }

    const { status: existingStatus } =
      await Notifications.getPermissionsAsync();

    let finalStatus = existingStatus;

    if (existingStatus !== 'granted') {
      const { status } = await Notifications.requestPermissionsAsync();
      finalStatus = status;
    }

    if (finalStatus !== 'granted') {
      __DEV__ && console.log('Push notifications: Permission not granted');
      return null;
    }

    const projectId =
      Constants?.expoConfig?.extra?.eas?.projectId ??
      Constants?.easConfig?.projectId;

    if (!projectId) {
      throw new Error('Push notifications: Project ID not found');
    }

    const { data: token } = await Notifications.getExpoPushTokenAsync({
      projectId,
    });

    __DEV__ && console.log('Device push notification token:', token);
    setPushToken(token);
    return token;
  }, [setPushToken]);

  const setupNotificationListeners = useCallback(() => {
    // Handle foreground notifications
    notificationListener.current =
      Notifications.addNotificationReceivedListener((notification) => {
        __DEV__ &&
          console.log('Notification received:', JSON.stringify(notification));
        setCurrentNotification(notification);

        // Don't automatically process deep links for foreground notifications
      });

    // Handle background/killed state notifications
    responseListener.current =
      Notifications.addNotificationResponseReceivedListener((response) => {
        __DEV__ &&
          console.log('Notification response:', JSON.stringify(response));
        setCurrentNotification(response.notification);

        // Only process deep links when user actually taps the notification
        if (response.notification.request.content.data?.deepLinkUrl) {
          handleDeepLink(
            response.notification.request.content.data.deepLinkUrl,
          );
        }
      });

    return () => {
      if (notificationListener.current) {
        Notifications.removeNotificationSubscription(
          notificationListener.current,
        );
      }
      if (responseListener.current) {
        Notifications.removeNotificationSubscription(responseListener.current);
      }
    };
  }, [setCurrentNotification, handleDeepLink]);

  const setupDeepLinkListeners = useCallback(() => {
    if (IS_WEB) {
      // On web, we handle initial URL capture for post-auth redirection
      handleWebRedirect();
      return () => {};
    }

    // Mobile-only deep link handling
    const getInitialURL = async () => {
      try {
        const initialUrl = await Linking.getInitialURL();
        if (initialUrl) {
          handleDeepLink(initialUrl);
        }
      } catch (error) {
        SentryService.captureError('Error getting initial URL', error);
      }
    };

    getInitialURL();

    // Handle new URLs while app is open
    urlListener.current = Linking.addEventListener('url', (event) => {
      handleDeepLink(event.url);
    });

    return () => {
      if (urlListener.current) {
        urlListener.current.remove();
      }
    };
  }, [handleDeepLink, handleWebRedirect]);

  useEffect(() => {
    if (IS_WEB) {
      __DEV__ &&
        console.log('Push notification listeners: Not supported on web');
      return;
    }

    const cleanup = setupNotificationListeners();
    return cleanup;
  }, [setupNotificationListeners]);

  useEffect(() => {
    const cleanup = setupDeepLinkListeners();
    return cleanup;
  }, [setupDeepLinkListeners]);

  useEffect(() => {
    const register = async () => {
      try {
        await registerForPushNotificationsAsync();
      } catch (error) {
        SentryService.captureError(
          'Error registering for push notifications',
          error,
        );
      }
    };

    register();
  }, [registerForPushNotificationsAsync]);
}
